/**
 * Client-side cache for Tolgee translations
 *
 * Features:
 * - In-memory cache with TTL (5 minutes default)
 * - localStorage persistence with longer TTL (1 hour default)
 * - LRU eviction when exceeding max languages
 * - Graceful error handling and fallback
 */

type TolgeeTranslations = Record<string, string>;

type CacheEntry = {
  data: TolgeeTranslations;
  expiresAt: number;
  cachedAt: number;
};

type StorageData = {
  version: number;
  entries: Record<string, { data: TolgeeTranslations; cachedAt: number }>;
};

// Configuration from environment variables
const MEMORY_TTL = parseInt(
  process.env.NEXT_PUBLIC_TOLGEE_CACHE_TTL_MEMORY || "300000"
); // 5 minutes
const STORAGE_TTL = parseInt(
  process.env.NEXT_PUBLIC_TOLGEE_CACHE_TTL_STORAGE || "3600000"
); // 1 hour
const MAX_CACHED_LANGUAGES = parseInt(
  process.env.NEXT_PUBLIC_TOLGEE_CACHE_MAX_LANGUAGES || "3"
);
const STORAGE_VERSION = 1;
const STORAGE_KEY = "tolgee_translations_cache_v1";
const DEBUG =
  process.env.NEXT_PUBLIC_TOLGEE_CACHE_DEBUG === "true" ||
  process.env.NODE_ENV === "development";

class TranslationsCache {
  private memoryCache: Map<string, CacheEntry>;
  private enableStorage: boolean;

  constructor(options?: { enableStorage?: boolean }) {
    this.memoryCache = new Map();
    this.enableStorage = options?.enableStorage ?? true;

    // Load from localStorage on init (client-only)
    if (this.enableStorage && typeof window !== "undefined") {
      this.loadFromStorage();
    }
  }

  /**
   * Get cached translations for a language
   * Returns null if not found or expired
   */
  get(language: string): TolgeeTranslations | null {
    // Check memory cache first
    const entry = this.memoryCache.get(language);
    if (entry && entry.expiresAt > Date.now()) {
      if (DEBUG) {
        console.log(`[Tolgee Cache] Memory HIT for language: ${language}`);
      }
      return entry.data;
    }

    // Clean up expired memory entry
    if (entry) {
      this.memoryCache.delete(language);
      if (DEBUG) {
        console.log(
          `[Tolgee Cache] Memory entry expired for language: ${language}`
        );
      }
    }

    // Check localStorage (if enabled)
    if (this.enableStorage && typeof window !== "undefined") {
      const storageData = this.getFromStorage(language);
      if (storageData) {
        if (DEBUG) {
          console.log(
            `[Tolgee Cache] localStorage HIT for language: ${language}, restoring to memory`
          );
        }
        // Restore to memory cache
        this.memoryCache.set(language, {
          data: storageData,
          expiresAt: Date.now() + MEMORY_TTL,
          cachedAt: Date.now(),
        });
        return storageData;
      }
    }

    if (DEBUG) {
      console.log(`[Tolgee Cache] MISS for language: ${language}`);
    }
    return null;
  }

  /**
   * Store translations in cache
   */
  set(language: string, data: TolgeeTranslations): void {
    const now = Date.now();

    // Store in memory
    this.memoryCache.set(language, {
      data,
      expiresAt: now + MEMORY_TTL,
      cachedAt: now,
    });

    if (DEBUG) {
      console.log(
        `[Tolgee Cache] Stored ${Object.keys(data).length} keys for language: ${language}`
      );
    }

    // Enforce size limit (LRU-style: remove oldest)
    if (this.memoryCache.size > MAX_CACHED_LANGUAGES) {
      const oldest = this.getOldestKey();
      if (oldest) {
        this.memoryCache.delete(oldest);
        if (DEBUG) {
          console.log(
            `[Tolgee Cache] LRU eviction: removed oldest language: ${oldest}`
          );
        }
      }
    }

    // Store in localStorage (async to avoid blocking)
    if (this.enableStorage && typeof window !== "undefined") {
      this.saveToStorage(language, data, now);
    }
  }

  /**
   * Clear all caches (memory + localStorage)
   */
  clear(): void {
    this.memoryCache.clear();
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(STORAGE_KEY);
        if (DEBUG) {
          console.log("[Tolgee Cache] Cleared all caches");
        }
      } catch (e) {
        console.error("[Tolgee Cache] Failed to clear localStorage:", e);
      }
    }
  }

  /**
   * Clear cached translations for a specific language
   */
  clearLanguage(language: string): void {
    this.memoryCache.delete(language);

    if (this.enableStorage && typeof window !== "undefined") {
      try {
        const stored = this.getAllFromStorage();
        if (stored) {
          delete stored[language];
          this.saveAllToStorage(stored);
          if (DEBUG) {
            console.log(
              `[Tolgee Cache] Cleared language from all caches: ${language}`
            );
          }
        }
      } catch (e) {
        console.error(
          `[Tolgee Cache] Failed to clear language ${language} from localStorage:`,
          e
        );
      }
    }
  }

  /**
   * Get the oldest cached language key (for LRU eviction)
   */
  private getOldestKey(): string | null {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;

    for (const [key, entry] of this.memoryCache.entries()) {
      if (entry.cachedAt < oldestTime) {
        oldestTime = entry.cachedAt;
        oldestKey = key;
      }
    }

    return oldestKey;
  }

  /**
   * Check if localStorage is available
   */
  private isStorageAvailable(): boolean {
    try {
      const test = "__tolgee_cache_test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Load all cached translations from localStorage
   */
  private loadFromStorage(): void {
    if (!this.isStorageAvailable()) {
      if (DEBUG) {
        console.warn(
          "[Tolgee Cache] localStorage not available, using memory-only mode"
        );
      }
      this.enableStorage = false;
      return;
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const parsed: StorageData = JSON.parse(raw);

      // Validate version
      if (!parsed.version || parsed.version !== STORAGE_VERSION) {
        if (DEBUG) {
          console.warn(
            "[Tolgee Cache] Storage version mismatch, clearing old data"
          );
        }
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Load non-expired entries
      const now = Date.now();
      let loadedCount = 0;

      for (const [language, entry] of Object.entries(parsed.entries)) {
        const age = now - entry.cachedAt;
        if (age < STORAGE_TTL) {
          // Don't load to memory yet, will be loaded on-demand via get()
          loadedCount++;
        }
      }

      if (DEBUG && loadedCount > 0) {
        console.log(
          `[Tolgee Cache] Loaded ${loadedCount} language(s) from localStorage`
        );
      }
    } catch (e) {
      console.error("[Tolgee Cache] Failed to load from localStorage:", e);
      // Clear corrupted data
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
  }

  /**
   * Get translations for a language from localStorage
   */
  private getFromStorage(language: string): TolgeeTranslations | null {
    if (!this.isStorageAvailable()) return null;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const parsed: StorageData = JSON.parse(raw);
      const entry = parsed.entries[language];

      if (!entry) return null;

      // Check if expired
      const age = Date.now() - entry.cachedAt;
      if (age >= STORAGE_TTL) {
        if (DEBUG) {
          console.log(
            `[Tolgee Cache] localStorage entry expired for language: ${language}`
          );
        }
        return null;
      }

      return entry.data;
    } catch (e) {
      console.error(
        `[Tolgee Cache] Failed to get ${language} from localStorage:`,
        e
      );
      return null;
    }
  }

  /**
   * Save translations for a language to localStorage
   */
  private saveToStorage(
    language: string,
    data: TolgeeTranslations,
    cachedAt: number
  ): void {
    if (!this.isStorageAvailable()) return;

    try {
      const existing = this.getAllFromStorage() || {};
      existing[language] = { data, cachedAt };
      this.saveAllToStorage(existing);
    } catch (e) {
      if (
        e instanceof Error &&
        (e.name === "QuotaExceededError" || e.name === "NS_ERROR_DOM_QUOTA_REACHED")
      ) {
        console.warn(
          "[Tolgee Cache] localStorage quota exceeded, clearing old entries"
        );
        // Try to clear and retry once
        this.clear();
        try {
          this.saveAllToStorage({ [language]: { data, cachedAt } });
        } catch {
          console.warn(
            "[Tolgee Cache] Still cannot save to localStorage, falling back to memory-only mode"
          );
          this.enableStorage = false;
        }
      } else {
        console.error("[Tolgee Cache] Failed to save to localStorage:", e);
      }
    }
  }

  /**
   * Get all cached translations from localStorage
   */
  private getAllFromStorage(): Record<
    string,
    { data: TolgeeTranslations; cachedAt: number }
  > | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const parsed: StorageData = JSON.parse(raw);
      return parsed.entries;
    } catch (e) {
      console.error("[Tolgee Cache] Failed to get all from localStorage:", e);
      return null;
    }
  }

  /**
   * Save all cached translations to localStorage
   */
  private saveAllToStorage(
    entries: Record<string, { data: TolgeeTranslations; cachedAt: number }>
  ): void {
    const storageData: StorageData = {
      version: STORAGE_VERSION,
      entries,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
  }
}

// Singleton instance
export const translationsCache = new TranslationsCache();
